import { useState, useEffect } from 'react';
import { platformTranslationService, TranslationKeys } from '../services/platformTranslationService';
import { useLanguage } from '../contexts/LanguageContext';

export function usePlatformTranslation() {
  const { currentLanguage } = useLanguage();
  const [translations, setTranslations] = useState<TranslationKeys | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationError, setTranslationError] = useState<string | null>(null);

  // Load translations for current language
  useEffect(() => {
    loadTranslations(currentLanguage);
  }, [currentLanguage]);

  // Listen for language change events
  useEffect(() => {
    const handleLanguageChange = (event: CustomEvent) => {
      loadTranslations(event.detail.language);
    };

    window.addEventListener('languageChanged', handleLanguageChange as EventListener);
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange as EventListener);
    };
  }, []);

  const loadTranslations = async (language: string) => {
    try {
      // Try to load from localStorage first
      const loaded = platformTranslationService.loadTranslationsFromStorage(language);
      if (loaded) {
        const translations = platformTranslationService.getTranslations(language);
        setTranslations(translations);
        return;
      }

      // If not available and not English, translate the platform
      if (language !== 'en') {
        setIsTranslating(true);
        setTranslationError(null);
        
        const success = await platformTranslationService.translatePlatform(language);
        if (success) {
          const translations = platformTranslationService.getTranslations(language);
          setTranslations(translations);
        } else {
          setTranslationError('Failed to translate platform');
          // Fallback to English
          const englishTranslations = platformTranslationService.getTranslations('en');
          setTranslations(englishTranslations);
        }
      } else {
        // Load English translations
        const englishTranslations = platformTranslationService.getTranslations('en');
        setTranslations(englishTranslations);
      }
    } catch (error) {
      console.error('Error loading translations:', error);
      setTranslationError('Failed to load translations');
      // Fallback to English
      const englishTranslations = platformTranslationService.getTranslations('en');
      setTranslations(englishTranslations);
    } finally {
      setIsTranslating(false);
    }
  };

  const t = (key: keyof TranslationKeys): string => {
    if (!translations) {
      return key; // Return key as fallback
    }
    return platformTranslationService.getTranslation(key, currentLanguage);
  };

  const retranslatePlatform = async () => {
    if (currentLanguage === 'en') return;
    
    setIsTranslating(true);
    setTranslationError(null);
    
    try {
      const success = await platformTranslationService.translatePlatform(currentLanguage);
      if (success) {
        const newTranslations = platformTranslationService.getTranslations(currentLanguage);
        setTranslations(newTranslations);
      } else {
        setTranslationError('Failed to retranslate platform');
      }
    } catch (error) {
      console.error('Retranslation error:', error);
      setTranslationError('Failed to retranslate platform');
    } finally {
      setIsTranslating(false);
    }
  };

  // Debug function to manually trigger translation
  const debugTranslate = async () => {
    await retranslatePlatform();
  };

  // Expose debug function to window for testing
  useEffect(() => {
    (window as any).debugTranslate = debugTranslate;
  }, [currentLanguage]);

  return {
    t,
    translations,
    isTranslating,
    translationError,
    retranslatePlatform
  };
}
